import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { MapProvider } from '@/components/MapProvider';
import { DeliveryMap } from '@/components/DeliveryMap';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Navigation, CheckCircle2, Clock, MapPin, Route as RouteIcon, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

export default function RouteManager({ user, onLogout }) {
  const navigate = useNavigate();
  const [pendingOrders, setPendingOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [riders, setRiders] = useState([]);
  const [selectedRider, setSelectedRider] = useState('');
  const [optimizing, setOptimizing] = useState(false);
  const [optimizedRoute, setOptimizedRoute] = useState(null);
  const [deliveryStops, setDeliveryStops] = useState([]);
  const [polylinePath, setPolylinePath] = useState([]);

  const fetchPendingOrders = useCallback(async () => {
    try {
      const response = await axios.get('/orders', {
        params: { status: 'READY_FOR_PICKUP' }
      });
      setPendingOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Error al cargar pedidos');
    }
  }, []);

  const fetchRiders = useCallback(async () => {
    try {
      const response = await axios.get('/users/riders');
      setRiders(response.data || []);
    } catch (error) {
      console.error('Error fetching riders:', error);
    }
  }, []);

  useEffect(() => {
    fetchPendingOrders();
    fetchRiders();
  }, [fetchPendingOrders, fetchRiders]);

  const toggleOrderSelection = (orderId) => {
    setSelectedOrders(prev => {
      if (prev.includes(orderId)) {
        return prev.filter(id => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };

  const decodePolyline = (encoded) => {
    const points = [];
    let index = 0, lat = 0, lng = 0;

    while (index < encoded.length) {
      let result = 0, shift = 0, b;
      
      do {
        b = encoded.charCodeAt(index++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);

      const dlat = ((result & 1) ? ~(result >> 1) : (result >> 1));
      lat += dlat;

      result = 0;
      shift = 0;
      
      do {
        b = encoded.charCodeAt(index++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);

      const dlng = ((result & 1) ? ~(result >> 1) : (result >> 1));
      lng += dlng;

      points.push({
        lat: lat / 1e5,
        lng: lng / 1e5
      });
    }

    return points;
  };

  const optimizeRoute = async () => {
    if (selectedOrders.length < 2) {
      toast.error('Selecciona al menos 2 pedidos para optimizar la ruta');
      return;
    }

    setOptimizing(true);
    try {
      const response = await axios.post('/orders/batch-optimize', {
        order_ids: selectedOrders,
        rider_id: selectedRider || undefined
      });

      setOptimizedRoute(response.data);
      
      // Preparar paradas para el mapa
      const stops = response.data.order_ids.map((orderId, index) => {
        const order = pendingOrders.find(o => o.id === orderId);
        return {
          id: orderId,
          address: order?.delivery_address?.line1 || 'Dirección no disponible',
          latitude: order?.delivery_address?.lat || 0,
          longitude: order?.delivery_address?.lng || 0,
          status: 'pending'
        };
      });
      
      setDeliveryStops(stops);

      // Decodificar polyline
      if (response.data.polyline) {
        const decoded = decodePolyline(response.data.polyline);
        setPolylinePath(decoded);
      }

      toast.success('Ruta optimizada correctamente');
      
      // Refrescar pedidos
      await fetchPendingOrders();
    } catch (error) {
      console.error('Error optimizing route:', error);
      toast.error('Error al optimizar ruta: ' + (error.response?.data?.detail || error.message));
    } finally {
      setOptimizing(false);
    }
  };

  const confirmRoute = async () => {
    toast.success('Ruta confirmada y asignada al rider');
    setSelectedOrders([]);
    setOptimizedRoute(null);
    await fetchPendingOrders();
  };

  return (
    <Layout user={user} onLogout={onLogout} title="Gestión de Rutas">
      <div className="p-6 bg-[#EAE0D5] min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
              className="text-[#224049]"
              title="Volver al Dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-3xl font-bold text-[#224049]">
              <Navigation className="inline mr-2" />
              Gestión de Rutas de Entrega
            </h1>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Panel izquierdo - Selección de pedidos */}
            <Card className="lg:col-span-1 bg-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" />
                  Pedidos Listos para Pickup
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Selector de Rider */}
                <div className="mb-4">
                  <label className="text-sm font-medium mb-2 block">
                    Asignar a Rider
                  </label>
                  <Select value={selectedRider} onValueChange={setSelectedRider}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar rider..." />
                    </SelectTrigger>
                    <SelectContent>
                      {riders.map(rider => (
                        <SelectItem key={rider.id} value={rider.id}>
                          {rider.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Lista de pedidos */}
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {pendingOrders.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">
                      No hay pedidos listos para pickup
                    </p>
                  ) : (
                    pendingOrders.map(order => (
                      <div
                        key={order.id}
                        className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition ${
                          selectedOrders.includes(order.id)
                            ? 'border-[#224049] bg-[#F5F1ED]'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => toggleOrderSelection(order.id)}
                      >
                        <Checkbox
                          checked={selectedOrders.includes(order.id)}
                          onCheckedChange={() => toggleOrderSelection(order.id)}
                        />
                        <div className="flex-1 text-sm">
                          <p className="font-medium">
                            {order.customer?.name || 'Sin nombre'}
                          </p>
                          <p className="text-gray-600 text-xs">
                            {order.delivery_address?.line1}
                          </p>
                          <p className="text-[#C38154] text-xs mt-1">
                            {order.mode === 'express' ? 'Express 60\'' : 'Estándar'}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <Button
                  onClick={optimizeRoute}
                  disabled={optimizing || selectedOrders.length < 2 || !selectedRider}
                  className="w-full mt-4 bg-[#224049] hover:bg-[#1a3138]"
                >
                  {optimizing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Optimizando...
                    </>
                  ) : (
                    <>
                      <RouteIcon className="w-4 h-4 mr-2" />
                      Optimizar Ruta ({selectedOrders.length})
                    </>
                  )}
                </Button>

                {optimizedRoute && (
                  <Card className="mt-4 bg-[#F5F1ED]">
                    <CardContent className="pt-4">
                      <h3 className="font-semibold mb-3 text-[#224049]">
                        Ruta Optimizada
                      </h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <RouteIcon className="w-4 h-4 text-[#C38154]" />
                          <span className="font-medium">Distancia:</span>
                          <span>{optimizedRoute.total_distance_km.toFixed(2)} km</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-[#C38154]" />
                          <span className="font-medium">Duración:</span>
                          <span>{Math.round(optimizedRoute.total_duration_minutes)} min</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-[#C38154]" />
                          <span className="font-medium">Paradas:</span>
                          <span>{optimizedRoute.order_ids.length}</span>
                        </div>
                      </div>

                      <Button
                        onClick={confirmRoute}
                        className="w-full mt-4 bg-[#0F7A5B] hover:bg-[#0d6549]"
                      >
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Confirmar Ruta
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </CardContent>
            </Card>

            {/* Panel derecho - Mapa */}
            <Card className="lg:col-span-2 bg-white h-[700px]">
              <CardContent className="p-0 h-full">
                <MapProvider>
                  <DeliveryMap
                    stops={deliveryStops}
                    polylinePath={polylinePath}
                    onMarkerClick={(stopId) => console.log('Marker clicked:', stopId)}
                  />
                </MapProvider>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}
